import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import { ScopeRequiredError } from "@nexus/widget-sdk";
import type { HeaderStat, JsonValue } from "@nexus/widget-sdk";
import {
  CATEGORY_HEATMAP_TABLE_CONTRACT,
  CDN_GATEKEEPER_RESPONSES_CONTRACT,
  EVENT_CDN_THIEVES_CONTRACT,
  EVENT_RESTREAMING_SERVICES_CONTRACT,
  FLOW_GEO_MAP_CONTRACT,
  GROUPED_METRICS_TABLE_CONTRACT,
  PIRACY_AUDIENCE_SERIES_CONTRACT,
  PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT,
  RESTREAMING_SERVER_BLOCKING_CONTRACT,
  SOCIAL_MEDIA_POSTS_CONTRACT,
} from "@nexus/widgets";
import type {
  CategoryHeatmapTableData,
  CdnGatekeeperResponsesData,
  EventCdnThievesData,
  EventEvolutionData,
  EventRestreamingServicesData,
  FlowGeoMapData,
  GroupedMetricsTableData,
  PiracyAudienceSeriesData,
  PirateSourceDistributionData,
  ProtectionOverviewCampaignEventsData,
  ProtectionOverviewCampaignsData,
  ProtectionOverviewEisData,
  RestreamingServerBlockingData,
  SocialMediaPostsData,
} from "@nexus/widgets";
import type {
  DataResolver,
  SourceMetricDefinition,
  SourceSelection,
} from "@/lib/dashboard/resolverTypes";
import type { GqlPiracyAudienceSeries } from "./reports";
import { normalizePiracyAudienceSeries, normalizeReportMetrics } from "./reports";

/**
 * Generated-report snapshot sources.
 *
 * Each source reads one immutable, versioned dataset of a published
 * `report_instance` (see `domains.reports.generator` on the backend for every
 * contract and its `scope_key` convention) and normalizes it into a widget
 * contract. Report dashboards are *templates*: their bindings name no report
 * edition and, on drill-down pages, no entity. Both come from the dashboard
 * context through `contextParams` — the edition (`report_instance_id`) from
 * the report page, the entity (`campaign_id`, `protected_event_id`) from the
 * route or the edition's own scope, `tenant_id` from a single-tenant edition —
 * so one template serves every edition of every report built on it.
 *
 * The browser never picks what it may read: the backend re-derives the
 * edition's tenant scope and authorizes it on every read, whatever is asked.
 *
 * Every source is a `report` source: widgets sharing an edition and an entity
 * share one deduplicated request (a KPI band and a section-header summary
 * over the same collection cost one read), exempt from the Live tenant and
 * time filters and refetched only on the report cadence.
 */

function requireReportInstanceId(value: JsonValue | undefined): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new ScopeRequiredError("Open this dashboard from a report.");
  }
  return value;
}

function requireEntity(value: JsonValue | undefined, entity: string): string {
  if ((typeof value !== "string" && typeof value !== "number") || value === "") {
    throw new ScopeRequiredError(`This report view needs a ${entity}.`);
  }
  return String(value);
}

type SnapshotEntity = "tenant" | "campaign" | "event";

/** The context param naming the entity each kind of dataset is keyed by. */
const ENTITY_CONTEXT: Record<SnapshotEntity, string> = {
  tenant: "tenant_id",
  campaign: "campaign_id",
  event: "protected_event_id",
};

/**
 * `scope_key` of the dataset to read:
 * - tenant-level contracts: the binding's static `scope_key` when it names
 *   one, else the context's `tenant_id`, else none (the edition-wide dataset);
 * - campaign and protected-event contracts: the entity from the context,
 *   suffixed by the binding's `group_id` when it has one (event trends).
 */
export function snapshotScopeKey(
  entity: SnapshotEntity,
  requestParams: Record<string, JsonValue>,
): string | null {
  if (entity === "tenant") {
    if (typeof requestParams.scope_key === "string" && requestParams.scope_key) {
      return requestParams.scope_key;
    }
    const tenantId = requestParams.tenant_id;
    return tenantId === undefined || tenantId === null || tenantId === "" ? null : String(tenantId);
  }
  const id = requireEntity(
    requestParams[ENTITY_CONTEXT[entity]],
    entity === "campaign" ? "campaign" : "protected event",
  );
  return typeof requestParams.group_id === "string" && requestParams.group_id
    ? `${id}:${requestParams.group_id}`
    : id;
}

const REPORT_SNAPSHOT_QUERY = gql`
  query ReportSnapshot(
    $report_instance_id: String!
    $contract_name: String!
    $contract_version: Int!
    $scope_key: String
    $selection: ReportSnapshotSelectionInput
  ) {
    report_snapshot(
      report_instance_id: $report_instance_id
      contract_name: $contract_name
      contract_version: $contract_version
      scope_key: $scope_key
      selection: $selection
    ) {
      generated_at
      payload
    }
  }
`;

interface GqlReportSnapshot {
  report_snapshot: { generated_at: string; payload: unknown };
}

async function fetchSnapshotPayload(
  params: Record<string, unknown>,
  client: GraphQLClient,
): Promise<unknown> {
  const raw = await client.request<GqlReportSnapshot>(REPORT_SNAPSHOT_QUERY, params);
  return raw.report_snapshot.payload;
}

// ── protection-overview-metrics / -campaign-metrics / -event-metrics ───────
//
// Shaped exactly like the shared `report-metrics` contract, with the optional
// `series` (a metric's monthly history) and `subject` (what the collection
// measures, used as a page heading) — so it renders with the generic
// `metric-kpi` and `metric-summary` widgets and `normalizeReportMetrics` from
// `./reports.ts` normalizes it: no report-specific type here.

interface RawProtectionOverviewFreshness {
  generated_at: string;
  data_through: string;
  source: string;
  is_estimated: boolean;
}

// ── protection-overview-tenant-eis ─────────────────────────────────────────

interface RawProtectionOverviewTenantHeader {
  id: number;
  label: string;
  region: string;
}

interface RawProtectionOverviewEisPayload {
  tenant: RawProtectionOverviewTenantHeader;
  eis_monthly: number[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEis(
  raw: RawProtectionOverviewEisPayload,
): ProtectionOverviewEisData {
  return {
    tenant: { id: raw.tenant.id, label: raw.tenant.label, region: raw.tenant.region },
    eisMonthly: raw.eis_monthly,
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-tenant-campaigns ───────────────────────────────────

interface RawProtectionOverviewCampaignsPayload {
  tenant: RawProtectionOverviewTenantHeader;
  campaigns: {
    id: string;
    label: string;
    start: string;
    end: string;
    avg_integrity: number;
    event_count: number;
    has_report: boolean;
  }[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewCampaigns(
  raw: RawProtectionOverviewCampaignsPayload,
): ProtectionOverviewCampaignsData {
  return {
    tenant: { id: raw.tenant.id, label: raw.tenant.label, region: raw.tenant.region },
    campaigns: raw.campaigns.map((campaign) => ({
      id: campaign.id,
      label: campaign.label,
      start: campaign.start,
      end: campaign.end,
      avgIntegrity: campaign.avg_integrity,
      eventCount: campaign.event_count,
      hasReport: campaign.has_report,
    })),
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-campaign-events ────────────────────────────────────

interface RawProtectionOverviewCampaignHeader {
  id: string;
  label: string;
  start: string;
  end: string;
  tenant?: { id: number; label: string } | null;
}

interface RawProtectionOverviewCampaignEvent {
  id: string;
  label: string;
  type: string;
  channel: string;
  start: string;
  end: string;
  popularity: number;
  integrity: number;
  has_report: boolean;
  collection_group?: string | null;
  collection?: string | null;
  logo?: string | null;
}

interface RawProtectionOverviewCampaignEventsPayload {
  campaign: RawProtectionOverviewCampaignHeader;
  events: RawProtectionOverviewCampaignEvent[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewCampaignEvents(
  raw: RawProtectionOverviewCampaignEventsPayload,
): ProtectionOverviewCampaignEventsData {
  return {
    campaign: {
      id: raw.campaign.id,
      label: raw.campaign.label,
      start: raw.campaign.start,
      end: raw.campaign.end,
      tenant: raw.campaign.tenant ?? undefined,
    },
    events: raw.events.map((event) => ({
      id: event.id,
      label: event.label,
      type: event.type,
      channel: event.channel,
      start: event.start,
      end: event.end,
      popularity: event.popularity,
      integrity: event.integrity,
      hasReport: event.has_report,
      collectionGroup: event.collection_group ?? null,
      collection: event.collection ?? null,
      logo: event.logo ?? null,
    })),
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-campaign-audience ──────────────────────────────────
//
// Reuses the `piracy-audience-trend` widget and its `piracy-audience-series`
// contract instead of a bespoke campaign widget: a report's audience section
// is the same block-safety time series the War Room already renders, plus an
// optional counterfactual series and a business-metrics summary — both are
// optional fields the shared contract added for exactly this case (see
// "Reuse before creating a widget" in `WIDGET_AUTHORING.md`). This normalizer
// wraps `normalizePiracyAudienceSeries` rather than duplicating its mapping.

interface RawCampaignAudienceSummary {
  avg_approx_audience: number;
  avg_estimated_audience: number;
  audience_reduction_pct: number;
  revenue_opportunities: number;
}

interface RawProtectionOverviewCampaignAudiencePayload extends GqlPiracyAudienceSeries {
  summary?: RawCampaignAudienceSummary | null;
}

export function normalizeProtectionOverviewCampaignAudience(
  raw: RawProtectionOverviewCampaignAudiencePayload,
): PiracyAudienceSeriesData {
  const points = raw.series ?? [];
  const base = normalizePiracyAudienceSeries({
    series: points.map((point) => ({
      bucket: point.bucket,
      safe_to_block_audience: point.safe_to_block_audience,
      unsafe_to_block_audience: point.unsafe_to_block_audience,
      observed_audience: point.observed_audience,
    })),
    blocking_intervals: raw.blocking_intervals,
    blocking_periods: raw.blocking_periods,
    freshness: raw.freshness,
  });
  return {
    ...base,
    points: base.points.map((point, index) => ({
      ...point,
      ...(points[index]?.estimated_audience === undefined
        ? {}
        : { estimated: points[index].estimated_audience }),
    })),
    ...(raw.summary === undefined
      ? {}
      : {
          reportMetrics: raw.summary
            ? {
                avgApproxAudience: raw.summary.avg_approx_audience,
                avgEstimatedAudience: raw.summary.avg_estimated_audience,
                audienceReductionPct: raw.summary.audience_reduction_pct,
                revenueOpportunities: raw.summary.revenue_opportunities,
              }
            : null,
        }),
  };
}

// ── protection-overview-campaign-per-event-kpi ─────────────────────────────
//
// Feeds the generic `grouped-metrics-table` widget rather than a bespoke
// per-event-KPI one: the widget contract names nothing about events,
// detections or engines, only column groups and metric columns, so this
// normalizer is the only place that knows "detections/responses/impact" is
// the shape of a campaign's per-event KPI matrix (see "Reuse before
// creating a widget" in `WIDGET_AUTHORING.md`).

interface RawPerEventEngineDetection {
  name: string;
  total_detections: number;
  detection_rate: number;
}

interface RawPerEventEngineResponse {
  name: string;
  response_rate: number;
  response_coverage_rate: number;
}

interface RawPerEventKpiRow {
  event_id: string;
  label: string;
  total_detections: number;
  avg_detection_rate: number;
  response_rate: number;
  response_coverage_rate: number;
  stream_unavail_pct: number;
  cdn_unavail_pct: number;
  engines: {
    detection: RawPerEventEngineDetection[];
    response: RawPerEventEngineResponse[];
  };
}

interface RawPerEventKpiSummary {
  total_detections: number;
  avg_detection_rate: number;
  response_rate: number;
  response_coverage_rate: number;
  stream_unavail_pct: number;
  cdn_unavail_pct: number;
}

interface RawProtectionOverviewCampaignPerEventKpiPayload {
  rows: RawPerEventKpiRow[];
  summary?: RawPerEventKpiSummary | null;
  freshness: RawProtectionOverviewFreshness;
}

function perEventKpiSummary(
  summary: RawPerEventKpiSummary | null | undefined,
): HeaderStat[] | undefined {
  if (!summary) return undefined;
  return [
    { label: "Detections", value: summary.total_detections, format: "compact" },
    { label: "Detection rate", value: summary.avg_detection_rate, format: "percent" },
    { label: "Response rate", value: summary.response_rate, format: "percent" },
    { label: "Coverage rate", value: summary.response_coverage_rate, format: "percent" },
    { label: "Stream unavail.", value: summary.stream_unavail_pct, format: "percent" },
    { label: "CDN unavail.", value: summary.cdn_unavail_pct, format: "percent" },
  ];
}

export function normalizeProtectionOverviewCampaignPerEventKpi(
  raw: RawProtectionOverviewCampaignPerEventKpiPayload,
): GroupedMetricsTableData {
  return {
    columnGroups: [
      {
        id: "detections",
        label: "Detections",
        accentColor: "--data-detection",
        columns: [
          { key: "totalDetections", label: "Total detections", kind: "count" },
          { key: "avgDetectionRate", label: "Avg. detection rate", kind: "percent" },
        ],
      },
      {
        id: "responses",
        label: "Responses",
        accentColor: "--data-response",
        columns: [
          { key: "responseRate", label: "Response rate", kind: "percent" },
          { key: "responseCoverageRate", label: "Response coverage", kind: "percent" },
        ],
      },
      {
        id: "impact",
        label: "Impact",
        accentColor: "--data-impact",
        columns: [
          { key: "streamUnavailPct", label: "Stream unavailability", kind: "percent" },
          { key: "cdnUnavailPct", label: "CDN unavailability", kind: "percent" },
        ],
      },
    ],
    rows: raw.rows.map((row) => ({
      id: row.event_id,
      label: row.label,
      values: {
        totalDetections: row.total_detections,
        avgDetectionRate: row.avg_detection_rate,
        responseRate: row.response_rate,
        responseCoverageRate: row.response_coverage_rate,
        streamUnavailPct: row.stream_unavail_pct,
        cdnUnavailPct: row.cdn_unavail_pct,
      },
      drilldown: [
        {
          label: "Detection engines",
          columnKeys: ["totalDetections", "avgDetectionRate"],
          rows: row.engines.detection.map((engine, index) => ({
            id: `${row.event_id}-detection-${index}`,
            label: engine.name,
            values: {
              totalDetections: engine.total_detections,
              avgDetectionRate: engine.detection_rate,
            },
          })),
        },
        {
          label: "Response engines",
          columnKeys: ["responseRate", "responseCoverageRate"],
          rows: row.engines.response.map((engine, index) => ({
            id: `${row.event_id}-response-${index}`,
            label: engine.name,
            values: {
              responseRate: engine.response_rate,
              responseCoverageRate: engine.response_coverage_rate,
            },
          })),
        },
      ],
    })),
    summary: perEventKpiSummary(raw.summary),
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-campaign-anomalies ─────────────────────────────────
//
// Feeds the generic `category-heatmap-table` widget: the existing
// `system-anomalies` widget is a live anomaly list, a different shape, so
// this is a genuinely new widget per the user's call — but its contract
// still names nothing about anomalies, severity or status, only column
// groups, columns and cell items. This normalizer is the only place that
// knows a campaign's system-anomaly markers become a severity x status
// cross-tab keyed by protected event.

interface RawAnomalyMarker {
  at: string;
  status: "active" | "resolved" | "acknowledged";
  severity: "critical" | "high" | "medium" | "low";
  label: string;
}

interface RawAnomalyLane {
  event_label: string;
  window: { start: string; end: string };
  markers: RawAnomalyMarker[];
}

interface RawProtectionOverviewCampaignAnomaliesPayload {
  lanes: RawAnomalyLane[];
  summary?: { total: number; active: number; resolved: number; acknowledged: number } | null;
  freshness: RawProtectionOverviewFreshness;
}

const ANOMALY_SEVERITY_COLUMNS = [
  { key: "critical", label: "Critical", colorToken: "--sev-critical" },
  { key: "high", label: "High", colorToken: "--sev-high" },
  { key: "medium", label: "Medium", colorToken: "--sev-medium" },
  { key: "low", label: "Low", colorToken: "--sev-low" },
] as const;

const ANOMALY_STATUS_COLUMNS = [
  { key: "active", label: "Active", colorToken: "--status-error" },
  { key: "resolved", label: "Resolved", colorToken: "--status-neutral" },
  { key: "acknowledged", label: "Acknowledged", colorToken: "--status-warn" },
] as const;

export function normalizeProtectionOverviewCampaignAnomalies(
  raw: RawProtectionOverviewCampaignAnomaliesPayload,
): CategoryHeatmapTableData {
  return {
    columnGroups: [
      {
        id: "severity",
        label: "Anomaly severity",
        columns: ANOMALY_SEVERITY_COLUMNS.map((column) => ({ ...column, heat: true })),
      },
      {
        id: "status",
        label: "Anomaly status",
        columns: ANOMALY_STATUS_COLUMNS.map((column) => ({ ...column, heat: false })),
      },
    ],
    rows: raw.lanes.map((lane, laneIndex) => {
      const cells: Record<string, { label: string; timestamp: string }[]> = {};
      for (const marker of lane.markers) {
        const item = { label: marker.label, timestamp: marker.at };
        (cells[marker.severity] ??= []).push(item);
        (cells[marker.status] ??= []).push(item);
      }
      return {
        id: `${lane.event_label}-${laneIndex}`,
        label: lane.event_label,
        cells,
      };
    }),
    summary: raw.summary
      ? [
          { label: "Total", value: raw.summary.total, format: "number" },
          {
            label: "Active",
            value: raw.summary.active,
            format: "number",
            tone: raw.summary.active > 0 ? "error" : "default",
          },
          { label: "Acknowledged", value: raw.summary.acknowledged, format: "number" },
          { label: "Resolved", value: raw.summary.resolved, format: "number" },
        ]
      : undefined,
  };
}

// ── protection-overview-campaign-piracy-intelligence ───────────────────────
//
// Feeds the new `flow-geo-map` widget: a pirate-service detection funnel
// correlated with a world map of CDN-theft IP locations, with hover/click
// highlighting shared live between the two — a genuinely new widget, not a
// bespoke report one, since that coupled interaction is what the widget's
// contract generalizes (see its doc comment in `types.ts`).

interface RawFlowNode {
  id: string;
  label: string;
  kind: "source" | "midpoint" | "sink";
}

interface RawFlowLink {
  source: string;
  target: string;
  value: number;
}

interface RawGeoAnchor {
  node_id: string;
  lng: number;
  lat: number;
}

interface RawGeoMarker {
  source_node_id: string;
  sink_node_id: string;
  lng: number;
  lat: number;
}

interface RawProtectionOverviewCampaignPiracyIntelligencePayload {
  nodes: RawFlowNode[];
  links: RawFlowLink[];
  anchors: RawGeoAnchor[];
  markers: RawGeoMarker[];
  summary?: {
    total_pirate_services: number;
    total_infrastructure: number;
    cdn_theft_services: number;
    restreaming_services: number;
  } | null;
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewCampaignPiracyIntelligence(
  raw: RawProtectionOverviewCampaignPiracyIntelligencePayload,
): FlowGeoMapData {
  return {
    nodes: raw.nodes.map((node) => ({ id: node.id, label: node.label, kind: node.kind })),
    links: raw.links.map((link) => ({
      source: link.source,
      target: link.target,
      value: link.value,
    })),
    anchors: raw.anchors.map((anchor) => ({
      nodeId: anchor.node_id,
      lng: anchor.lng,
      lat: anchor.lat,
    })),
    markers: raw.markers.map((marker) => ({
      sourceNodeId: marker.source_node_id,
      sinkNodeId: marker.sink_node_id,
      lng: marker.lng,
      lat: marker.lat,
    })),
    summary: raw.summary
      ? [
          { label: "Pirate services", value: raw.summary.total_pirate_services, format: "number" },
          { label: "Restreaming", value: raw.summary.restreaming_services, format: "number" },
          { label: "CDN theft", value: raw.summary.cdn_theft_services, format: "number" },
          { label: "CDN providers", value: raw.summary.total_infrastructure, format: "number" },
        ]
      : undefined,
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-event-pirate-source-distribution ──────────────────
//
// Reuses the existing `pirate-source-distribution` widget (donut
// presentation) instead of the previous hardcoded page's bespoke local
// chart: the backend computes `entries[]` directly in the widget's own
// contract shape, so this normalizer is a pass-through wrapper.

interface RawProtectionOverviewEventPirateSourceDistributionPayload {
  entries: PirateSourceDistributionData["entries"];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEventPirateSourceDistribution(
  raw: RawProtectionOverviewEventPirateSourceDistributionPayload,
): PirateSourceDistributionData {
  return { entries: raw.entries };
}

// ── protection-overview-event-evolution ────────────────────────────────────
//
// The published payload is already shaped exactly like the `event-evolution`
// widget's own `EventEvolutionData` contract (both use snake_case field
// names) — ported directly from the previous hardcoded page's
// `event.<id>.trends.json` fixture, so this normalizer only re-asserts the
// type rather than remapping fields.

export function normalizeProtectionOverviewEventEvolution(
  raw: EventEvolutionData,
): EventEvolutionData {
  return raw;
}

// ── protection-overview-event-restreaming-services ─────────────────────────

interface RawEventRestreamingService {
  id: string;
  label: string;
  distinct_ips: number;
  safe_ip_pct: number;
  approx_audience: number;
  estimated_audience: number;
  audience_reduction_pct: number;
}

interface RawEventHeader {
  id: string;
  label: string;
  campaign: { id: string; label: string };
  tenant?: { id: number; label: string } | null;
}

interface RawProtectionOverviewEventRestreamingServicesPayload {
  services: RawEventRestreamingService[];
  freshness: RawProtectionOverviewFreshness;
  event?: RawEventHeader | null;
}

export function normalizeProtectionOverviewEventRestreamingServices(
  raw: RawProtectionOverviewEventRestreamingServicesPayload,
): EventRestreamingServicesData {
  return {
    services: raw.services.map((service) => ({
      id: service.id,
      label: service.label,
      distinctIps: service.distinct_ips,
      safeIpPct: service.safe_ip_pct,
      approxAudience: service.approx_audience,
      estimatedAudience: service.estimated_audience,
      audienceReductionPct: service.audience_reduction_pct,
    })),
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
    event: raw.event
      ? {
          id: raw.event.id,
          label: raw.event.label,
          campaign: raw.event.campaign,
          tenant: raw.event.tenant ?? undefined,
        }
      : undefined,
  };
}

// ── protection-overview-event-cdn-thieves ──────────────────────────────────

interface RawEventCdnThief {
  id: string;
  label: string;
  distinct_ips: number;
  countries: number;
  accounts: number;
  channels: number;
  cdn_unavail_pct: number;
  unavail_duration_min: number;
}

interface RawProtectionOverviewEventCdnThievesPayload {
  thieves: RawEventCdnThief[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEventCdnThieves(
  raw: RawProtectionOverviewEventCdnThievesPayload,
): EventCdnThievesData {
  return {
    thieves: raw.thieves.map((thief) => ({
      id: thief.id,
      label: thief.label,
      distinctIps: thief.distinct_ips,
      countries: thief.countries,
      accounts: thief.accounts,
      channels: thief.channels,
      cdnUnavailPct: thief.cdn_unavail_pct,
      unavailDurationMin: thief.unavail_duration_min,
    })),
    freshness: {
      generatedAt: raw.freshness.generated_at,
      dataThrough: raw.freshness.data_through,
      source: raw.freshness.source,
      isEstimated: raw.freshness.is_estimated,
    },
  };
}

// ── protection-overview-event-cdn-gatekeeper ───────────────────────────────

interface RawCdnGatekeeperService {
  id: string;
  label: string;
  frames?: string[];
}

interface RawProtectionOverviewEventCdnGatekeeperPayload {
  response_type: string;
  response_rate: number;
  response_coverage_rate: number;
  trigger_step_index: number;
  step_count: number;
  services: RawCdnGatekeeperService[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEventCdnGatekeeper(
  raw: RawProtectionOverviewEventCdnGatekeeperPayload,
): CdnGatekeeperResponsesData {
  return {
    responseType: raw.response_type,
    responseRate: raw.response_rate,
    responseCoverageRate: raw.response_coverage_rate,
    triggerStepIndex: raw.trigger_step_index,
    stepCount: raw.step_count,
    services: raw.services.map((service) => ({
      id: service.id,
      label: service.label,
      ...(service.frames ? { frames: service.frames } : {}),
    })),
  };
}

// ── protection-overview-event-hermes ───────────────────────────────────────

interface RawHermesIsp {
  name: string;
  country_code: string;
  country_name: string;
  response_rate: number;
  response_coverage_rate: number;
  coverage_duration_min: number;
  flag?: string | null;
}

interface RawProtectionOverviewEventHermesPayload {
  response_rate: number;
  response_coverage_rate: number;
  isps: RawHermesIsp[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEventHermes(
  raw: RawProtectionOverviewEventHermesPayload,
): RestreamingServerBlockingData {
  return {
    responseRate: raw.response_rate,
    responseCoverageRate: raw.response_coverage_rate,
    isps: raw.isps.map((isp) => ({
      name: isp.name,
      countryCode: isp.country_code,
      countryName: isp.country_name,
      responseRate: isp.response_rate,
      responseCoverageRate: isp.response_coverage_rate,
      coverageDurationMin: isp.coverage_duration_min,
      flag: isp.flag ?? null,
    })),
  };
}

// ── protection-overview-event-social ───────────────────────────────────────

interface RawSocialMessage {
  author: string;
  handle: string;
  text: string;
  at: string;
}

interface RawSocialPlatform {
  platform: "x" | "reddit" | "telegram" | "facebook";
  label: string;
  message_count: number;
  messages: RawSocialMessage[];
  logo?: string | null;
}

interface RawProtectionOverviewEventSocialPayload {
  platforms: RawSocialPlatform[];
  freshness: RawProtectionOverviewFreshness;
}

export function normalizeProtectionOverviewEventSocial(
  raw: RawProtectionOverviewEventSocialPayload,
): SocialMediaPostsData {
  return {
    platforms: raw.platforms.map((platform) => ({
      platform: platform.platform,
      label: platform.label,
      messageCount: platform.message_count,
      logo: platform.logo ?? null,
      messages: platform.messages.map((message) => ({
        author: message.author,
        handle: message.handle,
        text: message.text,
        at: message.at,
      })),
    })),
  };
}

async function fetchSnapshotDefinitions(
  params: Record<string, unknown>,
  client: GraphQLClient,
): Promise<readonly SourceMetricDefinition[]> {
  const payload = await fetchSnapshotPayload(params, client);
  if (
    !payload ||
    typeof payload !== "object" ||
    !("metric_definitions" in payload) ||
    !Array.isArray(payload.metric_definitions)
  )
    throw new Error("Snapshot metric catalogue unavailable");
  return payload.metric_definitions.map((definition: unknown) => {
    if (
      !definition ||
      typeof definition !== "object" ||
      !("id" in definition) ||
      !("label" in definition) ||
      typeof definition.id !== "string" ||
      typeof definition.label !== "string"
    )
      throw new Error("Invalid snapshot metric definition");
    return { id: definition.id, label: definition.label };
  });
}

const SNAPSHOT_AUDIENCE_FIELDS: Record<string, string | null> = {
  points: "series",
  "points.safeToBlock": null,
  "points.unsafeToBlock": null,
  "points.observed": null,
  "points.estimated": null,
  blockingIntervals: "blocking_intervals",
  reportMetrics: "summary",
  freshness: "freshness",
  blockingPeriods: "blocking_periods",
  "blockingPeriods.id": "blocking_periods.id",
  "blockingPeriods.from": "blocking_periods.from_ts",
  "blockingPeriods.to": "blocking_periods.to_ts",
  "blockingPeriods.status": "blocking_periods.status",
  "blockingPeriods.viewersDisrupted": "blocking_periods.viewers_disrupted",
  // Accepted for live-source parity; snapshots do not record it (bands stay as published).
  blockingApplies: null,
};

function snapshotSource<Raw, Data>({
  label,
  description,
  contract,
  contractName,
  entity,
  normalize,
  extraParamKeys = [],
}: {
  label: string;
  description: string;
  contract: DataResolver["contract"];
  contractName: string;
  entity: SnapshotEntity;
  normalize: (raw: Raw) => Data;
  extraParamKeys?: readonly string[];
}): DataResolver {
  const buildParams: DataResolver["buildParams"] = (requestParams) => ({
    report_instance_id: requireReportInstanceId(requestParams.report_instance_id),
    scope_key: snapshotScopeKey(entity, requestParams),
    contract_name: contractName,
    contract_version: 1,
    ...(requestParams.selection === undefined ? {} : { selection: requestParams.selection }),
  });
  const selection: SourceSelection =
    contract.name === "report-metrics"
      ? {
          mode: "select",
          fields: ["metrics", "freshness", "subject"],
          history: true,
          catalogue: {
            buildParams: (requestParams, filters) => ({
              ...buildParams(requestParams, filters),
              selection: { definitions_only: true },
            }),
            fetcher: fetchSnapshotDefinitions,
          },
          toParams: ({ metricIds, historyMetricIds, includeComparison, fields }) => ({
            selection: {
              ...(metricIds === undefined ? {} : { metric_ids: [...metricIds] }),
              history_metric_ids: [...(historyMetricIds ?? [])],
              include_comparison: includeComparison ?? false,
              fields: [...(fields ?? [])],
            },
          }),
        }
      : contract.name === "piracy-audience-series"
        ? {
            mode: "select",
            fields: Object.keys(SNAPSHOT_AUDIENCE_FIELDS),
            toParams: ({ fields }) => ({
              selection: {
                fields: [
                  ...new Set(
                    (fields ?? [])
                      .map((field) => SNAPSHOT_AUDIENCE_FIELDS[field])
                      .filter((field): field is string => !!field),
                  ),
                ].sort(),
              },
            }),
          }
        : {
            mode: "complete",
            reason: `The bounded ${contract.name} dataset supplies its full table, ranking and drill-down context.`,
          };
  return {
    label,
    description,
    contract,
    selection,
    report: { paramKeys: ["report_instance_id", "scope_key", ...extraParamKeys] },
    contextParams: ["report_instance_id", ENTITY_CONTEXT[entity]],
    buildParams,
    fetcher: async (params, client: GraphQLClient) =>
      normalize((await fetchSnapshotPayload(params, client)) as Raw),
  };
}

const METRICS = { name: "report-metrics", version: 1 } as const;

export const reportSnapshotDataSources: Record<string, DataResolver> = {
  "protection-overview-metrics": snapshotSource({
    label: "Report - portfolio or tenant metrics (snapshot)",
    description:
      "KPI collection of one report edition: portfolio-wide, or one tenant's (static scope_key or the edition's tenant).",
    contract: METRICS,
    contractName: "protection-overview-metrics",
    entity: "tenant",
    normalize: normalizeReportMetrics,
  }),
  "protection-overview-tenant-eis": snapshotSource({
    label: "Report - tenant EIS trend (snapshot)",
    description: "Monthly EIS trend of one tenant in one report edition.",
    contract: { name: "protection-overview-tenant-eis", version: 1 },
    contractName: "protection-overview-tenant-eis",
    entity: "tenant",
    normalize: normalizeProtectionOverviewEis,
  }),
  "protection-overview-tenant-campaigns": snapshotSource({
    label: "Report - tenant campaigns (snapshot)",
    description: "Campaigns of one tenant in one report edition, with drill-down.",
    contract: { name: "protection-overview-tenant-campaigns", version: 1 },
    contractName: "protection-overview-tenant-campaigns",
    entity: "tenant",
    normalize: normalizeProtectionOverviewCampaigns,
  }),
  "protection-overview-campaign-metrics": snapshotSource({
    label: "Report - campaign metrics (snapshot)",
    description: "KPI collection of one campaign, with its title and dates as the page subject.",
    contract: METRICS,
    contractName: "protection-overview-campaign-metrics",
    entity: "campaign",
    normalize: normalizeReportMetrics,
  }),
  "protection-overview-campaign-events": snapshotSource({
    label: "Report - campaign protected events (snapshot)",
    description: "Protected events of one campaign, with drill-down.",
    contract: PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT,
    contractName: "protection-overview-campaign-events",
    entity: "campaign",
    normalize: normalizeProtectionOverviewCampaignEvents,
  }),
  "protection-overview-campaign-audience": snapshotSource({
    label: "Report - campaign pirate audience (snapshot)",
    description:
      "Pirate-audience trend of one campaign, with the counterfactual series and business-impact summary.",
    contract: PIRACY_AUDIENCE_SERIES_CONTRACT,
    contractName: "protection-overview-campaign-audience",
    entity: "campaign",
    normalize: normalizeProtectionOverviewCampaignAudience,
  }),
  "protection-overview-campaign-per-event-kpi": snapshotSource({
    label: "Report - campaign per-event KPIs (snapshot)",
    description: "Detection, response and impact KPIs of each protected event of one campaign.",
    contract: GROUPED_METRICS_TABLE_CONTRACT,
    contractName: "protection-overview-campaign-per-event-kpi",
    entity: "campaign",
    normalize: normalizeProtectionOverviewCampaignPerEventKpi,
  }),
  "protection-overview-campaign-anomalies": snapshotSource({
    label: "Report - campaign system anomalies (snapshot)",
    description: "System anomalies of one campaign, by protected event, severity and status.",
    contract: CATEGORY_HEATMAP_TABLE_CONTRACT,
    contractName: "protection-overview-campaign-anomalies",
    entity: "campaign",
    normalize: normalizeProtectionOverviewCampaignAnomalies,
  }),
  "protection-overview-campaign-piracy-intelligence": snapshotSource({
    label: "Report - campaign piracy intelligence (snapshot)",
    description: "Pirate-service funnel and CDN-theft map of one campaign.",
    contract: FLOW_GEO_MAP_CONTRACT,
    contractName: "protection-overview-campaign-piracy-intelligence",
    entity: "campaign",
    normalize: normalizeProtectionOverviewCampaignPiracyIntelligence,
  }),
  "protection-overview-event-metrics": snapshotSource({
    label: "Report - protected event metrics (snapshot)",
    description: "Header and per-engine KPIs of one protected event, with its page subject.",
    contract: METRICS,
    contractName: "protection-overview-event-metrics",
    entity: "event",
    normalize: normalizeReportMetrics,
  }),
  "protection-overview-event-pirate-source-distribution": snapshotSource({
    label: "Report - protected event pirate sources (snapshot)",
    description: "Share of detections by pirate source type for one protected event.",
    contract: { name: "pirate-source-distribution", version: 1 },
    contractName: "protection-overview-event-pirate-source-distribution",
    entity: "event",
    normalize: normalizeProtectionOverviewEventPirateSourceDistribution,
  }),
  "protection-overview-event-evolution": snapshotSource({
    label: "Report - protected event trends (snapshot)",
    description:
      "One detection-domain group's detection/response/impact series for one protected event (group_id param).",
    contract: { name: "event-evolution", version: 1 },
    contractName: "protection-overview-event-evolution",
    entity: "event",
    normalize: normalizeProtectionOverviewEventEvolution,
    extraParamKeys: ["group_id"],
  }),
  "protection-overview-event-restreaming-services": snapshotSource({
    label: "Report - protected event restreaming services (snapshot)",
    description: "Restreaming pirate services of one protected event.",
    contract: EVENT_RESTREAMING_SERVICES_CONTRACT,
    contractName: "protection-overview-event-restreaming-services",
    entity: "event",
    normalize: normalizeProtectionOverviewEventRestreamingServices,
  }),
  "protection-overview-event-cdn-thieves": snapshotSource({
    label: "Report - protected event CDN thieves (snapshot)",
    description: "CDN-theft infrastructure providers of one protected event.",
    contract: EVENT_CDN_THIEVES_CONTRACT,
    contractName: "protection-overview-event-cdn-thieves",
    entity: "event",
    normalize: normalizeProtectionOverviewEventCdnThieves,
  }),
  "protection-overview-event-cdn-gatekeeper": snapshotSource({
    label: "Report - protected event CDN Gatekeeper (snapshot)",
    description: "Automated CDN Gatekeeper response outcomes of one protected event.",
    contract: CDN_GATEKEEPER_RESPONSES_CONTRACT,
    contractName: "protection-overview-event-cdn-gatekeeper",
    entity: "event",
    normalize: normalizeProtectionOverviewEventCdnGatekeeper,
  }),
  "protection-overview-event-hermes": snapshotSource({
    label: "Report - protected event server blocking (snapshot)",
    description: "Restreaming-server (ISP) blocking outcomes of one protected event, by country.",
    contract: RESTREAMING_SERVER_BLOCKING_CONTRACT,
    contractName: "protection-overview-event-hermes",
    entity: "event",
    normalize: normalizeProtectionOverviewEventHermes,
  }),
  "protection-overview-event-social": snapshotSource({
    label: "Report - protected event social media (snapshot)",
    description: "Social-media posts about one protected event's piracy.",
    contract: SOCIAL_MEDIA_POSTS_CONTRACT,
    contractName: "protection-overview-event-social",
    entity: "event",
    normalize: normalizeProtectionOverviewEventSocial,
  }),
};
